import { inject, Injectable, OnDestroy } from '@angular/core';
import { SESSION_STORAGE_SYNC_SKIP_KEYS } from '../config/storage-tokens';

@Injectable({
	providedIn: 'root',
})
export class SessionStorageService implements OnDestroy {
	private readonly syncSkipKeys: string[] = inject(SESSION_STORAGE_SYNC_SKIP_KEYS);

	constructor() {
		window.addEventListener('storage', this.handleStorageEvent, false);
		this.requestSyncSessionStorage();
	}

	public getItem(key: string): string | null {
		return sessionStorage.getItem(key);
	}

	public setItem(key: string, value: string): void {
		sessionStorage.setItem(key, value);
	}

	public getSecureItem(key: string, decryptFn: (encrypted: string) => string): string | null {
		const encrypted = this.getItem(key);
		return encrypted ? decryptFn(encrypted) : null;
	}

	public setSecureItem(key: string, value: string, encryptFn: (value: string) => string): void {
		this.setItem(key, encryptFn(value));
	}

	public removeItem(key: string): void {
		sessionStorage.removeItem(key);
	}

	public clearAll(): void {
		sessionStorage.clear();
	}

	private handleStorageEvent = (event: StorageEvent): void => {
		if (event.key === 'requestSyncSessionStorage') {
			localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
			localStorage.removeItem('sessionStorage');
		} else if (event.key === 'sessionStorage') {
			const sessionStorageData = JSON.parse(event.newValue || '{}');
			for (const key in sessionStorageData) {
				if (!this.syncSkipKeys.includes(key)) {
					window.sessionStorage.setItem(key, sessionStorageData[key]);
				}
			}
		}
	};

	private requestSyncSessionStorage(): void {
		if (!sessionStorage.length) {
			const current = new Date().toLocaleTimeString();
			localStorage.setItem('requestSyncSessionStorage', 'request session storage' + current);
			localStorage.removeItem('requestSyncSessionStorage');
		}
	}

	ngOnDestroy(): void {
		window.removeEventListener('storage', this.handleStorageEvent, false);
	}
}
